import Head from 'next/head';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { useRouter } from 'next/router';

import Banner from '@components/molecules/banner/banner';
import SponsorBanner from '@components/molecules/sponsorBanner/sponsorBanner';
import ArchiveHeader from '@components/organisms/archive-header/archive-header';
import Header from '@components/organisms/header/header.withRedux';
import Footer from '@components/organisms/footer/footer.withRedux';
import SkipLink from '@components/atoms/skip-link/skipLink';
import Curtain from '@components/layout/curtain/curtain';

const TemplateDefault = ({
  children,
  noLogo,
  backgroundGradient = false,
  invertedHeader = false,
  invertedHeaderLogo = false,
  noDotsFooter,
  noFooter = false,
  hasFilter = false,
  hasCurtain = false,
  title,
  fallbackTitle,
  seo = {},
  stageType = 'default',
  banner,
  footer,
  navigation,
  jsonld,
  titleImage,
  status,
  isArchivePage = false,
  nonce = ''
}) => {
  const buildShareImage = imageSrc => (imageSrc ? `https://futurium.de${imageSrc}` : null);

  const router = useRouter();
  const isArchiveDetail = (status === 'expired' || router.query.archiveState) && !isArchivePage;

  return (
    <>
      <Head>
        <title>{`${title || seo.title || fallbackTitle} - Futurium`}</title>

        {seo.title && <meta name="title" content={seo.title} />}
        {seo.description && <meta name="description" content={seo.description} />}

        {seo.og && (
          <>
            <meta property="og:title" content={seo.og.title || seo.title} />
            <meta property="og:image" content={buildShareImage(seo.og.image || titleImage)} />
            <meta property="og:description" content={seo.og.description || seo.description} />
            <meta property="og:url" content={seo.og.url} />
            <meta property="og:type" content="website" />
          </>
        )}

        {seo.twitter && (
          <>
            <meta name="twitter:title" content={seo.twitter.title || seo.title} />
            <meta name="twitter:image" content={buildShareImage(seo.twitter.image || titleImage)} />
            <meta name="twitter:description" content={seo.twitter.description || seo.description} />
            <meta name="twitter:card" content={seo.twitter.card || 'summary'} />
          </>
        )}

        {jsonld ? (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonld }} />
        ) : null}
      </Head>

      {banner?.__typename === 'banner' && <Banner {...banner} />}
      {banner?.__typename === 'sponsorBanner' && router.asPath === '/' && (
        <SponsorBanner {...banner} />
      )}

      <div style={{ position: 'relative' }}>
        <SkipLink />

        {navigation && (
          <Header
            backgroundGradient={backgroundGradient}
            noLogo={noLogo}
            inverted={invertedHeader}
            hasFilter={hasFilter}
            invertedLogo={invertedHeaderLogo}
            stageType={stageType}
            disableScroll={isArchivePage}
            hasBanner={banner}
          />
        )}

        {isArchiveDetail && <ArchiveHeader />}
      </div>

      <main role="main" id="content">
        {children}
      </main>
      <script nonce={nonce} />
      {footer && !noFooter && <Footer noDots={noDotsFooter} isArchivePage={isArchivePage} />}
      {hasCurtain && <Curtain />}
    </>
  );
};

TemplateDefault.propTypes = {
  children: PropTypes.node,
  noLogo: PropTypes.bool,
  noDotsFooter: PropTypes.bool,
  noFooter: PropTypes.bool,
  hasFilter: PropTypes.bool,
  hasCurtain: PropTypes.bool,
  backgroundGradient: PropTypes.bool,
  invertedHeader: PropTypes.bool,
  invertedHeaderLogo: PropTypes.bool,
  title: PropTypes.string,
  fallbackTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  footer: PropTypes.shape(),
  navigation: PropTypes.arrayOf(PropTypes.shape()),
  seo: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    author: PropTypes.string,
    keywords: PropTypes.string,
    image: PropTypes.string,
    canonical: PropTypes.string,
    twitter: PropTypes.shape(),
    og: PropTypes.shape()
  }),
  stageType: PropTypes.string,
  banner: PropTypes.shape(),
  jsonld: PropTypes.string,
  status: PropTypes.string,
  titleImage: PropTypes.string,
  isArchivePage: PropTypes.bool,
  nonce: PropTypes.string
};

const mapStateToProps = ({ app: { pageData } }) => ({
  title: pageData.title,
  seo: pageData.seo,
  footer: pageData.footer,
  navigation: pageData.navigation,
  banner: pageData.banner,
  titleImage: pageData.titleImage ? pageData.titleImage.src : null,
  stageType: pageData.stageType,
  status: pageData.status,
  nonce: pageData.nonce
});

export default connect(mapStateToProps)(TemplateDefault);
